/**
 * markdownStyles — the shared assistant-markdown CSS rules.
 *
 * One definition consumed by both MessageBubble (assistant bubbles in
 * the chat scroll) and EditProposalMessage (the in-thread edit
 * proposal preview) so a drafted table/heading previews identically
 * to how the same markdown renders in a regular Asthra reply.
 *
 * Exported as a plain rule object (not a makeStyles hook) so each
 * consumer spreads it into its own ``makeStyles`` map — Fluent's
 * makeStyles requires literal-ish objects per component, and this
 * keeps the single source of truth without coupling hook identities.
 */

import { brandColors, fonts, inkColors, surfaceColors, typeScale } from "../tokens";

export const assistantMarkdownRules = {
  fontSize: typeScale.body,
  color: inkColors.ink,
  lineHeight: typeScale.bodyLineHeight,
  fontFamily: fonts.body,
  "& > *:first-child": { marginTop: 0 },
  "& > *:last-child": { marginBottom: 0 },
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    fontSize: typeScale.heading,
    fontWeight: 700,
    margin: "10px 0 4px",
  },
  "& p": { margin: "6px 0" },
  // Explicit list markers. The message <li> wrapper sets inline
  // ``list-style: none``, and list-style-type INHERITS, so without
  // re-declaring it here the markdown bullets/numbers vanish.
  "& ul, & ol": {
    margin: "6px 0",
    paddingLeft: "22px",
    listStylePosition: "outside",
  },
  "& ul": { listStyleType: "disc" },
  "& ol": { listStyleType: "decimal" },
  // Nested lists get distinct markers, matching typical prose.
  "& ul ul": { listStyleType: "circle", margin: "2px 0" },
  "& ul ul ul": { listStyleType: "square" },
  "& ol ol": { listStyleType: "lower-alpha", margin: "2px 0" },
  "& ol ol ol": { listStyleType: "lower-roman" },
  "& li": { margin: "2px 0" },
  "& li > p": { margin: 0 },
  // GFM task lists render a checkbox <input>; suppress the marker on
  // those rows and align the box with the text.
  "& li:has(> input[type='checkbox'])": { listStyleType: "none" },
  "& li > input[type='checkbox']": {
    marginRight: "6px",
    verticalAlign: "middle",
  },
  "& code": {
    fontFamily: fonts.mono,
    fontSize: typeScale.code,
    background: surfaceColors.paperDim,
    padding: "1px 4px",
    borderRadius: "3px",
  },
  "& pre": {
    fontFamily: fonts.mono,
    fontSize: typeScale.code,
    background: surfaceColors.paperDim,
    padding: "8px 10px",
    borderRadius: "3px",
    overflowX: "auto",
  },
  "& strong": { fontWeight: 700 },
  "& em": { fontStyle: "italic" },
  // ~sub~ / ^sup^ markers (remarkSubSup). line-height 0 stops the raised /
  // lowered glyph from stretching the line box.
  "& sub, & sup": { fontSize: "0.75em", lineHeight: 0 },
  "& a": { color: brandColors.primary, textDecoration: "underline" },
  "& blockquote": {
    borderLeft: `3px solid ${surfaceColors.lineStrong}`,
    paddingLeft: "8px",
    margin: "6px 0",
    color: inkColors.ink3,
  },
  "& table": {
    borderCollapse: "collapse",
    fontSize: typeScale.code,
    margin: "6px 0",
  },
  "& th, & td": {
    border: `1px solid ${surfaceColors.line}`,
    padding: "3px 6px",
  },
  "& th": {
    background: surfaceColors.paperDim,
    fontWeight: 700,
    textAlign: "left",
  },
  // repairPartialTables emits a blank header row for excerpts cut above
  // their header; hide it rather than show an empty grey band.
  "& thead:not(:has(th:not(:empty)))": { display: "none" },
  "& hr": {
    border: "none",
    borderTop: `1px solid ${surfaceColors.line}`,
    margin: "10px 0",
  },
} as const;
